import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Plug, Check, Info, ExternalLink, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function AwsCard({ aws }) {
  if (!aws) return null;
  return (
    <div className="border border-zinc-200 bg-zinc-50 p-6 mb-8" data-testid="aws-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="tx-overline">AWS</span>
            <span className="w-1 h-1 rounded-full bg-zinc-300" />
            {aws.configured ? (
              <span className="text-[10px] font-mono text-emerald-600" data-testid="aws-mode-badge">LIVE · {aws.region}</span>
            ) : (
              <span className="text-[10px] font-mono text-amber-600" data-testid="aws-mode-badge">SIMULATED · NO CREDENTIALS</span>
            )}
          </div>
          <div className="font-heading font-medium text-lg text-zinc-900">AWS Marketplace · AMMP & ACE</div>
        </div>
        <Cloud className="w-5 h-5 text-zinc-400" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-200 mb-4">
        <div className="p-4 md:border-r border-b md:border-b-0 border-zinc-200">
          <div className="tx-overline mb-1">AMMP · resale authorizations</div>
          <div className="text-[12px] text-zinc-700 mb-1">Marketplace Catalog API · <span className="font-mono">StartChangeSet → CreateResaleAuthorization</span></div>
          <div className={`text-[11px] font-mono ${aws.ammp_ready ? "text-emerald-600" : "text-amber-600"}`} data-testid="ammp-status">
            {aws.ammp_ready ? `READY · intent ${aws.intent}` : aws.configured ? "CREDENTIALS OK · AWS_MARKETPLACE_PRODUCT_ID missing" : "SIMULATED"}
          </div>
        </div>
        <div className="p-4">
          <div className="tx-overline mb-1">ACE · partner feed</div>
          <div className="text-[12px] text-zinc-700 mb-1">Partner Central Selling API · <span className="font-mono">ListEngagementInvitations + ListOpportunities</span></div>
          <div className={`text-[11px] font-mono ${aws.configured ? "text-emerald-600" : "text-amber-600"}`} data-testid="ace-status">
            {aws.configured ? "LIVE" : "SIMULATED"}
          </div>
        </div>
      </div>

      {!aws.ammp_ready && (
        <div className="border border-zinc-200 bg-white p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-[12px] text-zinc-600 leading-relaxed">
            Approved authorizations are <span className="text-amber-700">simulated</span> until an IAM user with
            <code className="text-orange-600 font-mono mx-1">aws-marketplace:StartChangeSet</code>,
            <code className="text-orange-600 font-mono mx-1">aws-marketplace:DescribeChangeSet</code> and
            <code className="text-orange-600 font-mono mx-1">partnercentral:List*</code> is configured in
            <code className="text-orange-600 font-mono mx-1">/app/backend/.env</code>:
            <pre className="mt-2 bg-zinc-50 p-3 rounded-sm text-[11px] text-zinc-700 border border-zinc-200">
{`AWS_ACCESS_KEY_ID=AKIA…
AWS_SECRET_ACCESS_KEY=…
AWS_REGION=us-east-1
AWS_MARKETPLACE_PRODUCT_ID=prod-xxxxxxxxxxxxx   # your listed SaaS/AMI product
AWS_CHANGESET_INTENT=VALIDATE                   # dry-run first; switch to APPLY to submit for real`}
            </pre>
            Restart the backend afterwards. Every submission records its mode (live / simulated) on the authorization.
          </div>
        </div>
      )}
    </div>
  );
}

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [aws, setAws] = useState(null);
  const [sf, setSf] = useState(null);

  const refresh = () => {
    endpoints.integrations().then((r) => { setItems(r.integrations); setAws(r.aws); });
    endpoints.sfStatus().then(setSf);
  };
  useEffect(refresh, []);

  useEffect(() => {
    // Auto-refresh after OAuth callback redirect
    const p = new URLSearchParams(window.location.search);
    if (p.get("sf_connected")) {
      toast.success("Salesforce connected");
      window.history.replaceState({}, "", "/integrations");
    } else if (p.get("sf_error")) {
      toast.error("Salesforce connection failed", { description: p.get("sf_error") });
    }
  }, []);

  const connect = async () => {
    try {
      const r = await endpoints.sfConnect();
      window.location.href = r.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salesforce not configured");
    }
  };

  const disconnect = async () => {
    await endpoints.sfDisconnect();
    toast("Salesforce disconnected");
    refresh();
  };

  return (
    <div className="max-w-[1200px] px-12 py-14" data-testid="page-integrations">
      <div className="tx-overline mb-2">Data plane</div>
      <h1 className="font-heading font-black text-4xl mb-2">Integrations</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Sources the autonomous agent draws from. Salesforce supports real OAuth;
        the rest are mocked with realistic enterprise sample data.
      </p>

      {/* Salesforce card */}
      <div className="border border-zinc-200 bg-zinc-50 p-6 mb-8" data-testid="salesforce-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="tx-overline">CRM</span>
              <span className="w-1 h-1 rounded-full bg-zinc-300" />
              {sf?.connected ? (
                <span className="text-[10px] font-mono text-emerald-600">LIVE OAUTH</span>
              ) : sf?.configured ? (
                <span className="text-[10px] font-mono text-amber-600">CONFIGURED · NOT CONNECTED</span>
              ) : (
                <span className="text-[10px] font-mono text-zinc-500">MOCKED FALLBACK</span>
              )}
            </div>
            <div className="font-heading font-medium text-lg text-zinc-900">Salesforce CRM</div>
            {sf?.connected && (
              <div className="text-[12px] text-zinc-500 font-mono mt-1">
                {sf.instance_url}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {sf?.connected ? (
              <Button
                variant="outline"
                onClick={disconnect}
                data-testid="sf-disconnect-btn"
                className="border-zinc-300 hover:bg-zinc-100 rounded-sm"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                onClick={connect}
                disabled={!sf?.configured}
                data-testid="sf-connect-btn"
                className="bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40 rounded-sm gap-2"
              >
                Connect Salesforce
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {!sf?.configured && (
          <div className="border border-zinc-200 bg-white p-4 flex items-start gap-3">
            <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-[12px] text-zinc-600 leading-relaxed">
              Salesforce OAuth is wired but not configured. Add the following to
              <code className="text-orange-600 font-mono mx-1">/app/backend/.env</code> and
              restart the backend:
              <pre className="mt-2 bg-zinc-50 p-3 rounded-sm text-[11px] text-zinc-700 border border-zinc-200">
{`SF_CLIENT_ID=<Consumer Key from your Connected App>
SF_CLIENT_SECRET=<Consumer Secret>
SF_LOGIN_URL=https://login.salesforce.com   # or https://test.salesforce.com for sandbox
SF_REDIRECT_URI=${window.location.origin}/api/integrations/salesforce/callback`}
              </pre>
              Until then, the app uses the mocked opportunity feed automatically.
            </div>
          </div>
        )}
      </div>

      {/* AWS card */}
      <AwsCard aws={aws} />

      {/* Other integrations */}
      <div className="tx-overline mb-3">Other sources</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-200">
        {items.filter((it) => !["salesforce", "aws_ace", "aws_ammp"].includes(it.key)).map((it, i, arr) => (
          <div
            key={it.key}
            data-testid={`integration-${it.key}`}
            className={`p-6 flex items-start justify-between ${i % 2 === 0 ? "md:border-r border-zinc-200" : ""} ${i < arr.length - 2 ? "border-b border-zinc-200" : "md:border-b-0 border-b border-zinc-200"}`}
          >
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="tx-overline">{it.kind}</span>
                <span className="w-1 h-1 rounded-full bg-zinc-300" />
                <span className="text-[10px] font-mono text-emerald-600">MOCKED</span>
              </div>
              <div className="font-heading font-medium text-lg text-zinc-900">{it.name}</div>
              <div className="text-[12px] text-zinc-500 font-mono mt-1">{it.record_count} sample records</div>
            </div>
            <div className="flex items-center gap-2 text-[11px] font-mono text-emerald-600">
              <Check className="w-3.5 h-3.5" /> Connected
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-zinc-200 p-6 bg-zinc-50">
        <div className="flex items-center gap-2 mb-2">
          <Plug className="w-4 h-4 text-orange-600" />
          <span className="tx-overline">Roadmap</span>
        </div>
        <p className="text-[13px] text-zinc-600 max-w-3xl">
          Salesforce OAuth and the AWS AMMP / ACE clients are wired for live mode and switch on
          automatically once credentials are present. MCP transport (SSE + JSON-RPC) to production
          Sales Agents remains mocked.
        </p>
      </div>
    </div>
  );
}
